import { useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { Link } from '@tanstack/react-router';
import { forgotPasswordSchema, type ForgotPasswordInput } from '@orion/contracts';
import { Button, Input } from '@orion/ui';
import { useAuth } from '../../lib/auth.js';
import { AuthShell, AuthStepCard, AuthStepEyebrow } from './AuthShell.js';

/** docs/architecture/05-authentication.md § "forgot/reset" — always shows
 * the same confirmation regardless of whether the email matched an account,
 * matching AuthService#requestPasswordReset's no-enumeration guarantee.
 *
 * design/orion-crm-design/rendered-html/ForgotPassword.html renders all 3
 * reset steps side by side as a single documentation composite — a real
 * visitor only ever sees one step at a time, so this (and ResetPasswordPage
 * for step 3) reuses that composite's exact card/input/typography values
 * without trying to reproduce its 3-abreast layout. */
export function ForgotPasswordPage() {
  const { requestPasswordReset } = useAuth();
  const [submittedEmail, setSubmittedEmail] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ForgotPasswordInput>({ resolver: zodResolver(forgotPasswordSchema) });

  const onSubmit = async (input: ForgotPasswordInput) => {
    await requestPasswordReset(input);
    setSubmittedEmail(input.email);
  };

  return (
    <AuthShell>
      {submittedEmail ? (
        <AuthStepCard>
          <AuthStepEyebrow step={2} of={3} label="Check your email" />
          <span className="flex size-12 items-center justify-center rounded-full bg-success-soft">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#1E7A4C" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M21 12V7a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h8" />
              <path d="m3 7 9 6 9-6" />
              <path d="m16 18 2 2 4-4" />
            </svg>
          </span>
          <div className="flex flex-col gap-1.5">
            <h1 className="text-[17px] leading-6 font-[650] tracking-[-0.01em] text-ink">Check your email</h1>
            <p className="text-[13px] leading-4.5 text-text-secondary">
              We sent a link to <strong className="font-semibold text-ink">{submittedEmail}</strong>. It expires in 30 minutes.
            </p>
          </div>
          <Link to="/login" className="self-center text-[13px] font-semibold text-primary-text hover:underline">
            Back to sign in
          </Link>
          <p className="border-t border-border pt-3 text-xs leading-4 text-text-muted">
            Nothing yet? Check spam, or ask your workspace admin to send a reset.
          </p>
        </AuthStepCard>
      ) : (
        <AuthStepCard>
          <AuthStepEyebrow step={1} of={3} label="Reset your password" />
          <div className="flex flex-col gap-1.5">
            <h1 className="text-[17px] leading-6 font-[650] tracking-[-0.01em] text-ink">Reset your password</h1>
            <p className="text-[13px] leading-4.5 text-text-secondary">Enter your work email and we&apos;ll send you a secure reset link.</p>
          </div>
          <form onSubmit={handleSubmit(onSubmit)} noValidate className="flex flex-col gap-4">
            <Input label="Work email" type="email" uiSize="lg" autoComplete="username" placeholder="name@oes.com" error={errors.email?.message} {...register('email')} />
            <Button type="submit" size="lg" className="w-full" loading={isSubmitting}>
              Send reset link
            </Button>
          </form>
          <Link to="/login" className="flex items-center justify-center gap-1.5 text-[13px] font-semibold text-primary-text hover:underline">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M19 12H5M11 6l-6 6 6 6" />
            </svg>
            Back to sign in
          </Link>
          <p className="border-t border-border pt-3 text-xs leading-4 text-text-muted">
            Signed in with Microsoft? Reset your password in your Microsoft account instead.
          </p>
        </AuthStepCard>
      )}
    </AuthShell>
  );
}
